"use client";

import React from "react";
import {
  useFieldArray,
  type ArrayPath,
  type Control,
  type FieldValues,
  type Path,
} from "react-hook-form";
import { Plus, X } from "lucide-react";
import { Button } from "../../../button";
import { Input } from "../../../input";
import { Label } from "../../../label";
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "../../../form";

/**
 * react-hook-form-controlled attendee email list. Generic over the per-provider
 * email key (`email` for Google, `emailAddress` for Microsoft) so a single
 * component serves both create flows instead of two near-duplicate files.
 */

interface RHFAttendeeFieldsProps<T extends FieldValues> {
  control: Control<T>;
  /** The field-array path, e.g. "attendees". */
  name: ArrayPath<T>;
  /** Property holding the email on each row: "email" or "emailAddress". */
  emailKey: string;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
}

export function RHFAttendeeFields<T extends FieldValues>({
  control,
  name,
  emailKey,
  label = "Attendees",
  placeholder = "name@example.com",
  disabled,
}: RHFAttendeeFieldsProps<T>) {
  const { fields, append, remove } = useFieldArray<T>({ control, name });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">{label}</Label>
        <span className="text-xs text-muted-foreground">
          {fields.length === 0 ? "None" : `${fields.length}`}
        </span>
      </div>

      {fields.length > 0 && (
        <div className="space-y-2">
          {fields.map((row, index) => (
            <FormField
              key={row.id}
              control={control}
              name={`${name}.${index}.${emailKey}` as Path<T>}
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <div className="flex items-center gap-2">
                    <FormControl>
                      <Input
                        {...field}
                        value={(field.value as string) ?? ""}
                        placeholder={placeholder}
                        disabled={disabled}
                      />
                    </FormControl>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={disabled}
                      onClick={() => remove(index)}
                      aria-label="Remove attendee"
                      className="shrink-0 text-muted-foreground hover:text-destructive"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          ))}
        </div>
      )}

      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={disabled}
        onClick={() =>
          // RHF 7.82+ exports a FieldArray *component* that shadows the old
          // FieldArray type — cast via append's parameter type instead.
          append({ [emailKey]: "" } as Parameters<typeof append>[0])
        }
        className="w-full"
      >
        <Plus className="mr-2 h-4 w-4" />
        Add attendee
      </Button>
    </div>
  );
}

export default RHFAttendeeFields;
